import { Shell } from "@/components/layout/Shell";
import { creditosLivres } from "@shared/credito-saldo";
import { useAuth } from "@/hooks/use-auth";
import { StatCard } from "@/components/ui/stat-card";
import { cn } from "@/lib/utils";
import { FileText, Building2, CheckCircle, Clock, Wallet, Bell } from "lucide-react";
import {
  useLaudoStats,
  useLaudos,
  useEmpresas,
  useUsuarios,
  useAllLaudos,
  useCreditosSaldo,
  useCreditosTransacoesPorEmpresa,
  useNotificacoes,
  useMarcarNotificacaoLida,
} from "@/hooks/use-data";
import { useModulos } from "@/hooks/use-modulos";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LAUDO_STATUS_LABELS, LAUDO_STATUS_COLORS } from "@/lib/constants";
import { rotuloCartela, tituloLaudo } from "@/lib/laudo-identificador";
import { useMemo } from "react";
import { useLocation } from "wouter";

export default function Dashboard() {
  const { profile } = useAuth();
  const [, navigate] = useLocation();
  const empresaId = profile?.empresa?.id;
  const { modulos } = useModulos();

  const isOwner = profile?.roles.includes("owner") || false;
  const isParceiro = profile?.roles.includes("parceiro_negocios") || false;
  const vistoriadoraId = isOwner || isParceiro ? undefined : empresaId;
  const parceiroId = isParceiro ? empresaId : undefined;
  const { data: stats, isLoading: statsLoading } = useLaudoStats(
    vistoriadoraId,
    parceiroId,
  );
  const { data: laudos, isLoading: laudosLoading } = isOwner
    ? useAllLaudos()
    : useLaudos(vistoriadoraId, undefined, undefined, undefined, parceiroId);
  const { data: empresas } = useEmpresas();
  const { data: usuarios } = useUsuarios(empresaId);

  // Créditos — só carrega para parceiros com módulo ativo
  const creditosAtivos = isParceiro && modulos.creditosPrePagos;
  const { data: saldo } = useCreditosSaldo(
    creditosAtivos ? empresaId : undefined,
  );
  const { data: transacoesCredito } = useCreditosTransacoesPorEmpresa(
    creditosAtivos ? empresaId : undefined,
  );

  const totalCreditosDisponivel = useMemo(
    () => saldo?.reduce((s, b) => s + creditosLivres(b), 0) ?? 0,
    [saldo],
  );
  const totalCreditosReservado = saldo?.reduce((s, b) => s + (b.reservado ?? 0), 0) ?? 0;
  const creditosGastosEsteMes = useMemo(() => {
    if (!transacoesCredito) return 0;
    const now = new Date();
    return transacoesCredito
      .filter((t) => {
        const isEntrada = ["compra", "liberacao", "estorno_credito"].includes(
          t.tipo,
        );
        if (isEntrada) return false;
        const d = new Date(t.criado_em);
        return (
          d.getFullYear() === now.getFullYear() &&
          d.getMonth() === now.getMonth()
        );
      })
      .reduce((s, t) => s + t.quantidade, 0);
  }, [transacoesCredito]);

  const recentLaudos = laudos?.slice(0, 5) || [];

  const { data: notificacoes } = useNotificacoes();
  const marcarLida = useMarcarNotificacaoLida();
  const pendencias = notificacoes?.filter((n) => !n.lida).slice(0, 6) ?? [];

  return (
    <Shell>
      <div className="mb-8">
        <h1
          className="text-3xl font-bold font-display text-slate-900"
          data-testid="text-page-title"
        >
          Painel de Controle
        </h1>
        <p className="text-muted-foreground mt-1">
          Bem-vindo de volta, {profile?.nome}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        {statsLoading ? (
          Array(4)
            .fill(0)
            .map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)
        ) : (
          <>
            <StatCard
              title="Total de Laudos"
              value={stats?.total || 0}
              icon={FileText}
              iconClassName="bg-blue-500/10 text-blue-600"
            />
            <StatCard
              title="Laudos Finalizados"
              value={stats?.finalizado || 0}
              icon={CheckCircle}
              iconClassName="bg-emerald-500/10 text-emerald-600"
            />
            <StatCard
              title="Laudos Pendentes"
              value={
                (stats?.rascunho || 0) +
                (stats?.enviado_analise || 0) +
                (stats?.em_vistoria || 0) +
                (stats?.em_avaliacao_rt || 0)
              }
              icon={Clock}
              iconClassName="bg-amber-500/10 text-amber-600"
            />
            {creditosAtivos ? (
              <div
                className="cursor-pointer"
                onClick={() => navigate("/creditos")}
              >
                <StatCard
                  title="Créditos livres"
                  value={totalCreditosDisponivel}
                  icon={Wallet}
                  iconClassName="bg-amber-500/10 text-amber-600"
                  trend={`${totalCreditosReservado} reservado(s) · ${creditosGastosEsteMes} gasto(s) este mês`}
                  trendUp={false}
                />
              </div>
            ) : !isParceiro ? (
              <StatCard
                title="Parceiros de Negócios"
                value={
                  empresas?.filter((e) => e.tipo === "PARCEIRO_DE_NEGOCIOS")
                    .length || 0
                }
                icon={Building2}
                iconClassName="bg-purple-500/10 text-purple-600"
              />
            ) : (
              <div></div>
            )}
          </>
        )}
      </div>

      {pendencias.length > 0 && (
        <Card className="mb-6 border-none shadow-lg shadow-slate-200/50">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0">
            <Bell className="w-4 h-4 text-amber-600" />
            <CardTitle>Pendências</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2">
              {pendencias.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    marcarLida.mutate(n.id);
                    if (n.laudoDbId) navigate(`/laudos/${n.laudoDbId}`);
                  }}
                  className="text-left flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100 hover:border-amber-300 transition-colors"
                >
                  <span className="mt-1 w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {n.titulo}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {n.mensagem}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-7">
        <Card className="lg:col-span-4 border-none shadow-lg shadow-slate-200/50">
          <CardHeader>
            <CardTitle>Resumo por Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statsLoading
                ? Array(6)
                    .fill(0)
                    .map((_, i) => <Skeleton key={i} className="h-10 w-full" />)
                : Object.entries(LAUDO_STATUS_LABELS).map(([key, label]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                    >
                      <span className="text-sm font-medium text-slate-700">
                        {label}
                      </span>
                      <span
                        className={cn(
                          "px-3 py-1 rounded-full text-xs font-bold",
                          LAUDO_STATUS_COLORS[key],
                        )}
                      >
                        {stats?.[key] || 0}
                      </span>
                    </div>
                  ))}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 border-none shadow-lg shadow-slate-200/50">
          <CardHeader>
            <CardTitle>Laudos Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {laudosLoading ? (
                Array(3)
                  .fill(0)
                  .map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
              ) : recentLaudos.length > 0 ? (
                recentLaudos.map((laudo) => (
                  <div
                    key={laudo.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {tituloLaudo(laudo)}
                      </p>
                      {rotuloCartela(laudo.cartela) && (
                        <p className="text-xs text-muted-foreground">
                          {rotuloCartela(laudo.cartela)}
                        </p>
                      )}
                    </div>
                    <span
                      className={cn(
                        "px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap",
                        LAUDO_STATUS_COLORS[laudo.status] ||
                          "bg-slate-100 text-slate-700",
                      )}
                    >
                      {LAUDO_STATUS_LABELS[laudo.status] || laudo.status}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Nenhum laudo encontrado
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
